import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "zo-install-dismissed";
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const t = setTimeout(() => { if (isIOS()) setShow(true); }, 6000);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); clearTimeout(t); };
  }, []);

  const dismiss = () => { setShow(false); setIosHelp(false); localStorage.setItem(DISMISS_KEY, "1"); };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setShow(false);
      setDeferred(null);
    } else {
      setIosHelp(true);
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
          data-testid="install-prompt"
          className="fixed bottom-36 sm:bottom-20 left-4 right-4 sm:left-6 sm:right-auto sm:w-96 z-[70] bg-elevate border border-white/15 clip-frame p-4 shadow-2xl">
          <button onClick={dismiss} data-testid="install-dismiss" aria-label="Dismiss" className="absolute top-3 right-3 text-gray-500 hover:text-blood"><X size={16} /></button>
          <div className="flex gap-4">
            <img src="/icons/icon-192.png" alt="" className="w-14 h-14 shrink-0 border border-white/10" />
            <div className="min-w-0">
              <p className="font-display font-bold uppercase text-sm leading-tight">Get the Zo Pound app</p>
              {!iosHelp ? (
                <>
                  <p className="mt-1 text-xs text-gray-400">Add it to your home screen — full-screen, one tap, no app store.</p>
                  <button onClick={install} data-testid="install-button"
                    className="mt-3 inline-flex items-center gap-2 bg-blood hover:bg-red-700 text-white font-display font-bold uppercase tracking-wider text-xs px-4 py-2 transition-colors">
                    <Download size={14} /> Install
                  </button>
                </>
              ) : (
                <p className="mt-1 text-xs text-gray-300 leading-relaxed" data-testid="install-ios-help">
                  Tap <Share size={12} className="inline -mt-0.5 text-blood" /> <span className="text-white">Share</span> in your browser bar, then <span className="text-white">“Add to Home Screen”</span>.
                </p>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
